import { create } from 'zustand';
import { mockProfile } from '../lib/mock';

const KEY = 'asia_profile';

interface Persisted {
  email: string;
  pinSet: boolean;
  biometricEnabled: boolean;
}

/** Load the persisted profile, seeded from mockProfile on first launch. */
const read = (): Persisted => {
  const seed: Persisted = {
    email: mockProfile.email ?? '',
    pinSet: mockProfile.pinSet ?? false,
    biometricEnabled: mockProfile.biometricEnabled ?? false,
  };
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...seed, ...(JSON.parse(raw) as Partial<Persisted>) } : seed;
  } catch {
    return seed;
  }
};

const write = (p: Persisted) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable */
  }
};

interface ProfileState extends Persisted {
  setEmail: (email: string) => void;
  setPinSet: (pinSet: boolean) => void;
  setBiometric: (biometricEnabled: boolean) => void;
}

/** Account state shown on the Profile screen — email, PIN and biometrics.
 *  Persisted to localStorage so the rows reflect real, durable status (mock
 *  phase; phase 3 replaces this with the API-backed profile). */
export const useProfile = create<ProfileState>((set, get) => {
  const persist = () => {
    const { email, pinSet, biometricEnabled } = get();
    write({ email, pinSet, biometricEnabled });
  };
  return {
    ...read(),
    setEmail: (email) => {
      set({ email });
      persist();
    },
    setPinSet: (pinSet) => {
      set({ pinSet });
      persist();
    },
    setBiometric: (biometricEnabled) => {
      set({ biometricEnabled });
      persist();
    },
  };
});
